import type { IConfigStore } from '../../interfaces/store-config.interface';
import { get } from 'svelte/store';
import { WidgetType } from '../../enums/widget-type.enum';
import { WindowsCustomEventTypes } from '../../enums/windows-custom-event.enum';
import { appendComponents } from '../../helpers/append-component';
import { updateConfigStore } from '../../helpers/update-config-store';
import { updateStateStore } from '../../helpers/update-state-store';
import type { IAnswer } from '../../interfaces/answer.interface';
import { ConfigStore } from '../../store/config';
import {
  getOptionConfigDefaults,
  type ConfigDefaultsVersion,
} from '../../store/config-defaults';
import {
  state_conversationResults,
  state_searchString,
  state_showSearch,
} from '../../store/state';
import { defaultLogger as logger } from '../../telemetry/logger';
import { GetOption } from '../api/get-option';

export class ConfigHandler {
  private init: boolean = false;

  constructor() {}

  public async get(params: {
    configOptions: IConfigStore;
    serverConfig: {
      config: string;
      tenant: string;
      agent?: string;
    };
  }) {
    try {
      // An initialization without an override must leave tenant selection intact,
      // including when a previous initialization supplied an agent.
      const agent = params?.serverConfig
        ? params.serverConfig.agent?.trim() || undefined
        : get(ConfigStore)?.serverConfig?.agent_id;
      const res = await GetOption(
        params?.serverConfig?.config ??
          get(ConfigStore)?.serverConfig?.id ??
          '',
        params?.serverConfig?.tenant ||
          get(ConfigStore)?.serverConfig?.tenant_id ||
          '',
        agent || '',
        params?.configOptions.apiBaseUrl || get(ConfigStore).apiBaseUrl || '',
        params?.configOptions?.trackWebLoads === true
          ? params.configOptions.customAttributes ?? {}
          : undefined
      );

      if (res?.ok) {
        const data: any = await res.json();
        const optionConfig = data?.widget?.config ?? {};

        if (data?.widget?.config || data?.defaults_version === 2)
          updateConfigStore({
            ...optionConfig,
            ...getOptionConfigDefaults(
              data?.defaults_version as ConfigDefaultsVersion | undefined,
              optionConfig
            ),
          });

        if (data?.widget)
          updateConfigStore({
            ...get(ConfigStore),
            widget: data.widget,
          });

        if (data?.widget?.state) updateStateStore(data?.widget?.state);

        if (params) {
          const config: IConfigStore = {
            ...get(ConfigStore),
            ...params.configOptions,
            serverConfig: {
              ...get(ConfigStore).serverConfig,
              id: params?.serverConfig?.config,
              resolved_id: data?.id,
              tenant_id: params?.serverConfig?.tenant,
              agent_id: agent,
              name: data?.name,
              conversation_prompt: data?.conversation_prompt,
              defaults_version: data?.defaults_version,
              question_prompts: data?.question_prompts,
              document_prompts: data?.document_prompts,
              updated_date: data?.updated_date,
            },
          };
          updateConfigStore(config);

          this.requestGreeting();
        }

        this.isInit();
      }
    } catch (e) {
      logger.error(
        'Cannot load chatbot configuration - chatbot will not show',
        e
      );

      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.configError, {
          detail: e,
        })
      );
    }
  }

  /** method to append widget to the DOM */
  private isInit(): void {
    if (!this.init) {
      appendComponents();
      this.init = true;
    }
  }

  private requestGreeting(): void {
    switch (get(ConfigStore).widgetType) {
      case WidgetType.chat:
      case WidgetType.cip:
      case WidgetType.ce:
        window.dispatchEvent(
          new CustomEvent(WindowsCustomEventTypes.requestGreeting)
        );
      default:
        break;
    }
  }
}
